// authored by Colin Reitman and Elvis Guillen
import React, { useRef, useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import ResizeObserver from 'react-resize-observer';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';
import { useTheme } from 'emotion-theming';

import Button from '../Button';
import Title from '../Title';
import Text from '../Text';
import {
  BorderRadius,
  BoxShadow,
  TransitionGlobal,
} from '../../GlobalStyles';

const getModalStyle = (minHeight = '400px', customBorderRadius = BorderRadius, customMaxWidth = '650px', widthFitContent, overlayBackgroundColor) => ({
  overlay: {
    alignItems: 'center',
    backgroundColor: overlayBackgroundColor,
    bottom: 0,
    display: 'flex',
    height: '100vh',
    overflowY: 'auto',
    position: 'fixed',
    width: '100vw',
    zIndex: '100',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: customBorderRadius,
    boxShadow: `${BoxShadow}`,
    display: 'flex',
    flexDirection: 'column',
    margin: '20px',
    marginTop: '40px',
    maxWidth: customMaxWidth,
    minHeight,
    minWidth: `${widthFitContent ? '' : '440px'}`,
    width: `${widthFitContent ? '' : '60vw'}`,
  },
});

export const ModalContentWrapper = styled('div')`
  background-color: ${(props) => props.theme.backgroundColorL2};
  border-radius: ${(props) => props.customBorderRadius || 'initial'};
  display: flex;
  flex-grow: 1;
  flex-grow: ${(props) => (props.flexGrow ? 1 : 0)};
  padding: ${(props) => props.customPadding || '40px'};
  
  form {
    padding-bottom: 20px;

    label {
      color: ${(props) => props.theme.textColor};
    }

    input, textarea {
      background-color: ${(props) => props.theme.backgroundColorL3};
    }
  }

  .modal-button {
    bottom: 0;
    border-bottom-left-radius: ${(props) => props.customBorderRadius};
    border-bottom-right-radius: ${(props) => props.customBorderRadius};
    color: ${(props) => (props.customButton ? 'white !important' : '')};
    left: 0px;
    position: absolute;
  }

  .modal-button-cancel {
    left: 0;
  }

  .modal-button-custom {
    margin-left: 0;
    width: 50%;
  }

  .modal-button-ok {
    right: 0;
  }

  .react-pdf__Page__canvas {
    margin: 0 auto;
  }

  .select__control, .select__menu, textarea {
    background-color: ${(props) => props.theme.backgroundColorL3};
    border-color: ${(props) => props.theme.noBorderColorForm};
    color: ${(props) => props.theme.textColor};
  }

  .select__single-value {
    color: ${(props) => props.theme.textColor};
  }

  .spinner-container {
    min-height: 300px;
  }

  @media only screen and (max-height : 580px) {
    padding: 20px;

    .title-container {
      display: none;
    }

    textarea {
      min-height: 120px;
    }
  }

  @media only screen and (min-height: 581px) and (max-height : 768px) {
    padding: ${(props) => props.customPadding || '20px'};

    .title-container {
      margin-bottom: 0px;
    }

    textarea {
      min-height: 120px;
    }
  }

  @media only screen and (max-width: 1024px) {
    .note-title-container {
      margin-bottom: 0px!important;
    }
  }

  @media screen and (max-width: 767px) {
    padding: ${(props) => props.customPadding || '20px'};

    .title-container {
      margin-bottom: 0px;
    }

    textarea {
      min-height: 120px;
    }
  }
`;

const TitleContainer = styled('div')`
  border-bottom: ${(props) => (props.titleBorderBottomStyle ? props.titleBorderBottomStyle : '1px solid #979797')};
  padding-top: 40px; 
  width: 100%;

  @media only screen and (max-width: 767px) {
    padding-top: 20px;
  }
`;

const SharedModal = ({
  children = '',
  closeOnClickOutside = false,
  customBorderRadius = 'initial',
  customButton = false,
  customMaxWidth = '650px',
  customMinHeight = '400px',
  customPadding = '40px',
  flexGrow = true,
  headerHeightToSubtract = 0,
  hideButton = false,
  hideTitle = false,
  isOpen,
  onRequestClose,
  overlayBackgroundColor = 'rgba(216, 216, 216, 0.5)',
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = true,
  sidebarWidth = 0,
  subtitle = '',
  title = '',
  titleBorderBottomStyle = '1px solid #979797',
  titleIcon = '',
  widthFitContent = false,
}) => {
  const [modalHeightDimension, setModalHeightDimension] = useState({});
  const [modalTitleHeight, setModalTitleHeight] = useState({});
  const modalContentRef = useRef(null);
  const modalRef = useRef(null);
  const titleContainerRef = useRef(null);
  const theme = useTheme();

  const modalClose = () => {
    onRequestClose();
  };

  const handleClickOutside = (event) => {
    if (
      modalRef.current
      && !modalRef.current.contains(event.target)
    ) {
      modalClose();
    }
  };

  useEffect(() => {
    if (isOpen && closeOnClickOutside) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeOnClickOutside]);

  useEffect(() => {
    if (titleContainerRef?.current) {
      setModalTitleHeight(titleContainerRef.current.getBoundingClientRect().height);
    }
  }, [subtitle, title]);

  const modalStyle = getModalStyle(
    customMinHeight,
    customBorderRadius,
    customMaxWidth,
    widthFitContent,
    overlayBackgroundColor,
  );

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Content {
            opacity: 0;
            transform: translateY(50%);

            &::-webkit-scrollbar {
                display: none;
              }

              /* For Firefox */
              scrollbar-width: none;

              /* For IE and Edge */
              -ms-overflow-style: none;
            
            &:focus {
              outline-color: -webkit-focus-ring-color!important;
              outline-style: auto!important;
              outline-width: 0px!important;
              outline: none!important;
            }
            @media screen and (max-width: 580px) {
              min-width: ${widthFitContent ? '' : '350px!important'};
              padding-bottom: ${customPadding || '60px'};
            }

          }

          .ReactModal__Overlay {
            height: calc(100vh - ${headerHeightToSubtract || 0}px)!important;
            width: calc(100vw - ${sidebarWidth || 0}px)!important;
            margin-left: ${sidebarWidth || 0}px!important;
            justify-content: center;
            opacity: 1;
            z-index: 1000!important;

            @media screen and (max-width: 767px) {
              margin-left: 0px!important;
              width: 100vw!important;
            }
          }

          .ReactModal__Overlay--after-open {
            opacity: 1;
            transition: ${TransitionGlobal};
          }

          .ReactModal__Overlay--before-close {
            opacity: 0;
          }

          .ReactModal__Content--after-open {
            opacity: 1;
            transform: translateY(0);
            transition: ${TransitionGlobal};
          }

          .ReactModal__Content--before-close {
            opacity: 0;
            transform: translateY(50%);
          }

            @media screen and (max-height: ${title ? modalHeightDimension + (modalTitleHeight + 80) : modalHeightDimension}px) {
            .ReactModal__Content {
              position: absolute!important;
              top: 15px!important;
            }
          }

          @media only screen and (max-device-width: 767px) {
            .ReactModal__Content {
              min-width: ${widthFitContent ? '' : '95%!important'};
            }
          }
        `}
      />
      <ReactModal
        className='global-modal'
        closeTimeoutMS={250}
        contentRef={(node) => { modalRef.current = node; }}
        isOpen={isOpen}
        onRequestClose={modalClose}
        overlayClassName='overlay'
        shouldCloseOnEsc={shouldCloseOnEsc}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        style={modalStyle}
      >
        {title && !hideTitle && (
          <TitleContainer titleBorderBottomStyle={titleBorderBottomStyle} ref={titleContainerRef}>
            <ResizeObserver
              onResize={(rect) => {
                setModalTitleHeight(rect?.height);
              }}
            />
            <Title icon={titleIcon}>
              {title}
              {subtitle && (
                <Text style={{
                  fontWeight: 600, marginTop: '5px', overflowWrap: 'break-word', paddingLeft: '20px', paddingRight: '20px', whiteSpace: 'normal', wordBreak: 'break-word',
                }}
                >
                  {subtitle}
                </Text>
              )}
            </Title>
          </TitleContainer>
        )}
        <ModalContentWrapper
          customBorderRadius={customBorderRadius}
          customButton={customButton}
          customPadding={customPadding}
          flexGrow={flexGrow}
          ref={modalContentRef}
        >
          <ResizeObserver
            onResize={(rect) => {
              setModalHeightDimension(rect?.height);
            }}
          />
          {children}
          {!customButton && !hideButton && (
            <Button
              bottom
              cta='Close'
              customColor={theme.colors.green}
              customTextColor='white'
              fullWidth
              large
              noBorder
              onClick={modalClose}
              primary
              square
              className='modal-button'
            />
          )}
        </ModalContentWrapper>
      </ReactModal>
    </>
  );
};

if (process.env.STORYBOOK_RUNNING === undefined) {
  ReactModal.setAppElement('#app');
}

SharedModal.propTypes = {
  children: PropTypes.node,
  closeOnClickOutside: PropTypes.bool,
  customBorderRadius: PropTypes.string,
  customButton: PropTypes.bool,
  customMaxWidth: PropTypes.string,
  customMinHeight: PropTypes.string,
  customPadding: PropTypes.string,
  flexGrow: PropTypes.bool,
  headerHeightToSubtract: PropTypes.number,
  hideButton: PropTypes.bool,
  hideTitle: PropTypes.bool,
  isOpen: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]).isRequired,
  onRequestClose: PropTypes.func.isRequired,
  overlayBackgroundColor: PropTypes.string,
  shouldCloseOnEsc: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
  sidebarWidth: PropTypes.number,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  titleBorderBottomStyle: PropTypes.string,
  titleIcon: PropTypes.string,
  widthFitContent: PropTypes.bool,
};

SharedModal.defaultProps = {
  children: '',
  closeOnClickOutside: false,
  customBorderRadius: 'initial',
  customButton: false,
  customMaxWidth: '650px',
  customMinHeight: '400px',
  customPadding: '40px',
  flexGrow: true,
  headerHeightToSubtract: 0,
  hideButton: false,
  hideTitle: false,
  overlayBackgroundColor: 'rgba(216, 216, 216, 0.5)',
  shouldCloseOnEsc: true,
  shouldCloseOnOverlayClick: true,
  sidebarWidth: 0,
  subtitle: '',
  title: '',
  titleBorderBottomStyle: '1px solid #979797',
  titleIcon: '',
  widthFitContent: false,
};

export default SharedModal;
